import {
  studioErrorEnglish,
  studioErrorWelsh,
} from '../../data/models/Studio/studioErrorText';
import {getLocalizationBasedOnLang} from '../../data/models/User/UserInfo';

/*
 * Text shared across Microreel Studio: what authors see when a request fails, the time
 * estimates while Studio drafts or generates, and the words of the components every Studio
 * page shares (the page head, job status, microreel list and renaming). Messages the Studio
 * server writes for authors arrive in its own words and are shown as they are.
 */

const english = {
  // A failed request: the generic message, a pending credit check, Studio missing from this
  // environment and a storyboard changed elsewhere (the data layer's studioErrorText).
  ...studioErrorEnglish,
  renderBlocked:
    "Microreels can't be generated right now. Please try again later.",
  shareFailed:
    'The video was saved but could not be shared. Try sharing again.',
  courseCategoriesFailed: 'Could not load course categories.',

  // A credit check: AI actions wait until the AI credits a stopped generation holds settle,
  // because the workspace needs more of them or the generation's cost is still being
  // confirmed (see creditCheck). `credits` is an amount such as "12 AI credits", in whole
  // credits as the sidebar's balance and the marketplace show them. Authors who cannot buy
  // credits (the marketplace needs Manage settings) are asked to go to an administrator.
  aiCredits: (count: number) =>
    `${count.toLocaleString('en-US')} AI ${count === 1 ? 'credit' : 'credits'}`,
  creditCheckCredits: (credits: string | null) =>
    `Your last generation used more AI credits than your workspace had left. Add ${
      credits || 'AI credits'
    } and this unlocks on its own within a minute.`,
  creditCheckAskCredits: (credits: string | null) =>
    `Your last generation used more AI credits than your workspace had left. Ask your workspace administrator to add ${
      credits || 'AI credits'
    }, and this unlocks on its own within a minute.`,
  // The workspace has enough now (e.g. just topped up): only the settlement itself is left.
  creditCheckCovered:
    'Your workspace has enough AI credits now. This unlocks on its own within a minute.',
  creditCheckPendingAsk:
    "The AI credits from your last generation haven't been settled yet. If your workspace has run out of AI credits, ask your workspace administrator to add more, then check again.",
  // A cost still unknown can need an administrator, so the wait says who to go to.
  creditCheckReceipt:
    "We're confirming how many AI credits your last generation used. This usually takes a minute or two. If it hasn't cleared in a few minutes, contact your workspace administrator.",
  // The cost stayed unknown: only Tapybl's team can record it, so adding credits cannot help.
  creditCheckReview:
    "We couldn't confirm how many AI credits your last generation used, and adding AI credits won't unlock this. Contact our support team to have it checked, then check again.",
  // Checking again left the credits unsettled, said by why they wait (see creditCheck's
  // creditsNotSettled): a minute helps only while the cost or the settlement itself is left.
  creditsNotSettled:
    "The AI credits still can't be settled. Check again in a minute.",
  creditsNotSettledCredits:
    "The AI credits still can't be settled: your workspace needs more of them first.",
  creditsNotSettledReview:
    "The AI credits still can't be settled. Our support team needs to check them first.",
  // A request refused for too few AI credits, for an author who cannot buy them.
  askForAiCredits: 'Ask your workspace administrator to add AI credits.',
  addAiCredits: 'Add AI credits',
  // The link's name for screen readers: it opens in a new tab.
  addAiCreditsLabel: 'Add AI credits (opens in a new tab)',
  checkAgain: 'Check again',
  checkingCredits: 'Checking credits…',

  // Time estimates. `time` is a rounded duration such as "40 seconds" or "3 minutes".
  seconds: (count: number) => `${count} seconds`,
  minutes: (count: number) => `${count} ${count === 1 ? 'minute' : 'minutes'}`,
  aboutTime: (time: string) => `about ${time}`,
  timeLeft: (time: string) => `About ${time} left`,
  readyIn: (aboutTime: string) => `Ready in ${aboutTime}`,
  almostReady: 'Almost ready…',
  almostDone: 'Almost done…',
  takingLonger: 'Taking a little longer than usual',
  thisOne: (time: string) => `About ${time} for this one`,
  thisOneAlmostDone: 'This one is almost done',
  thisOneAndAll: (thisOne: string, aboutTime: string, count: number) =>
    `${thisOne} · ${aboutTime} for ${count === 2 ? 'both' : `all ${count}`}`,
  allReels: (time: string, count: number) =>
    `About ${time} for ${count === 2 ? 'both' : `all ${count}`} microreels`,

  // The page head.
  allMicroreels: 'All microreels',
  beta: 'Beta',
  betaFeature: 'Beta feature',

  // A job's status, as its badge names it.
  statusDrafting: 'Drafting',
  statusChecking: 'Checking',
  statusChooseTopics: 'Choose topics',
  statusStoryboardReady: 'Storyboard ready',
  statusApproved: 'Approved',
  statusGenerating: 'Generating',
  statusReady: 'Ready',
  statusFailed: 'Failed',

  // What Studio is doing right now, beside Cancel.
  workCanceling: 'Canceling…',
  workDrafting: 'Drafting storyboard…',
  workStarting: 'Starting generation…',
  workGenerating: 'Generating…',
  workFinishing: 'Finishing up…',
  working: 'Working…',
  // Server stages said the way the rest of Studio says them; `…` follows each.
  stageStarting: 'Starting generation',
  stageFinishing: 'Finishing up',
  stageExporting: 'Exporting microreel ',
  // A video job's source stages, where older servers named a document.
  stageTranscribingVideo: 'Transcribing the video',
  stageFindingVideoTopics: 'Finding the topics in the video',
  readyCount: (ready: number, total: number) => `${ready} of ${total} ready`,
  cancel: 'Cancel',
  cancelGeneration: 'Cancel generation',

  // A render that stopped part way: generating again keeps the microreels it made and makes
  // the remaining ones (see partialRender).
  finishMicroreels: 'Finish your microreels',
  finishHint: (ready: number, count: number, remaining: number) =>
    `${ready} of ${count} microreels ${
      ready === 1 ? 'is' : 'are'
    } ready. Generating keeps ${
      ready === 1 ? 'it' : 'them'
    } and makes the remaining ${remaining === 1 ? 'one' : remaining}.`,
  // A render that stopped after it made every microreel, before it finished them (see
  // keepsEveryReel).
  finishAll: (count: number): string =>
    count === 1
      ? 'Generating keeps your microreel and finishes it.'
      : `Generating keeps ${
          count === 2 ? 'both' : `all ${count}`
        } microreels and finishes them.`,
  generateRemaining: (remaining: number): string =>
    remaining === 1
      ? 'Generate the remaining microreel'
      : `Generate the remaining ${remaining} microreels`,
  madeKept: (ready: number): string =>
    ready === 1
      ? 'The microreel already made is kept.'
      : 'The microreels already made are kept.',
  // Generating again from a failed render's alert: it keeps the microreels the render made
  // and makes only the rest, or finishes them when it made them all (see keepsEveryReel).
  continueGenerating: 'Continue',
  continuingGeneration: 'Continuing…',
  continueMakes: (remaining: number, kept: number): string =>
    remaining === 0
      ? kept === 1
        ? 'Continue finishes it.'
        : 'Continue finishes them.'
      : remaining === 1
      ? 'Continue makes only the remaining one.'
      : `Continue makes only the remaining ${remaining}.`,
  // An older server stopped a render whose narration ran past the length it aims for, which is
  // only an estimate (see jobError). `seconds` and `estimate` are whole seconds; `cut` is the
  // words it asked to cut, for when it stops there again.
  ranPastEstimate: (
    title: string,
    seconds: number,
    estimate: number,
    cut?: number,
  ): string =>
    `“${title}” runs ${seconds}s, longer than its estimated ${estimate}s, and generating stopped there.${
      cut
        ? ` If generating stops there again, cut about ${cut} ${
            cut === 1 ? 'word' : 'words'
          } from its narration.`
        : ''
    }`,

  // Renaming the microreels (the group) or one microreel in place.
  renameMicroreel: 'Rename the microreel',
  renameMicroreels: 'Rename the microreels',
  microreelName: 'Name of the microreel',
  microreelsName: 'Name of the microreels',
  enterMicroreelName: 'Enter a name for the microreel.',
  enterMicroreelsName: 'Enter a name for the microreels.',

  // The microreel list beside the player or storyboard.
  nowPlaying: 'Now playing',
  editing: 'Editing',
  switchMicroreel: 'Switch microreel',
  previousMicroreel: 'Previous microreel',
  nextMicroreel: 'Next microreel',
  pagerPosition: (current: number, count: number) => `${current} / ${count}`,

  // A render blocker, named by the microreel and the scene or clip it is about.
  sceneNoun: 'scene',
  clipNoun: 'clip',
  /** "Microreel 3 (Using a fire extinguisher), scene 2"; the title and scene are optional. */
  blockerLabel: (reel: number, title?: string, noun?: string, scene?: number) =>
    `Microreel ${reel}${title ? ` (${title})` : ''}${
      noun && scene ? `, ${noun} ${scene}` : ''
    }`,
  confirmFlaggedBlocker:
    'Please confirm the flagged item at the top of its storyboard.',
};

const welsh: typeof english = {
  ...studioErrorWelsh,
  renderBlocked:
    'Nid oes modd cynhyrchu microreels ar hyn o bryd. Rhowch gynnig arall arni yn nes ymlaen.',
  shareFailed:
    'Cafodd y fideo ei gadw ond nid oedd modd ei rannu. Rhowch gynnig ar ei rannu eto.',
  courseCategoriesFailed: 'Nid oedd modd llwytho’r categorïau cyrsiau.',

  // Welsh counts take the singular noun: "120 credyd AI".
  aiCredits: (count: number) => `${count.toLocaleString('en-US')} credyd AI`,
  creditCheckCredits: (credits: string | null) =>
    `Defnyddiodd eich cynhyrchiad diwethaf fwy o gredydau AI nag oedd gan eich gweithle ar ôl. ${
      credits ? `Ychwanegwch ${credits}` : 'Ychwanegwch gredydau AI'
    } a bydd hyn yn datgloi’n awtomatig o fewn munud.`,
  creditCheckAskCredits: (credits: string | null) =>
    `Defnyddiodd eich cynhyrchiad diwethaf fwy o gredydau AI nag oedd gan eich gweithle ar ôl. Gofynnwch i weinyddwr eich gweithle ychwanegu ${
      credits || 'credydau AI'
    }, a bydd hyn yn datgloi’n awtomatig o fewn munud.`,
  creditCheckCovered:
    'Mae gan eich gweithle ddigon o gredydau AI bellach. Bydd hyn yn datgloi’n awtomatig o fewn munud.',
  creditCheckPendingAsk:
    'Nid yw credydau AI eich cynhyrchiad diwethaf wedi’u setlo eto. Os yw eich gweithle wedi rhedeg allan o gredydau AI, gofynnwch i weinyddwr eich gweithle ychwanegu rhagor, yna gwiriwch eto.',
  creditCheckReceipt:
    'Rydym yn cadarnhau faint o gredydau AI a ddefnyddiodd eich cynhyrchiad diwethaf. Mae hyn fel arfer yn cymryd munud neu ddwy. Os nad yw wedi clirio ymhen ychydig funudau, cysylltwch â gweinyddwr eich gweithle.',
  creditCheckReview:
    'Nid oedd modd i ni gadarnhau faint o gredydau AI a ddefnyddiodd eich cynhyrchiad diwethaf, ac ni fydd ychwanegu credydau AI yn datgloi hyn. Cysylltwch â’n tîm cymorth i’w wirio, yna gwiriwch eto.',
  creditsNotSettled:
    'Nid oes modd setlo’r credydau AI o hyd. Gwiriwch eto ymhen munud.',
  creditsNotSettledCredits:
    'Nid oes modd setlo’r credydau AI o hyd: mae angen rhagor ohonynt ar eich gweithle yn gyntaf.',
  creditsNotSettledReview:
    'Nid oes modd setlo’r credydau AI o hyd. Mae angen i’n tîm cymorth eu gwirio yn gyntaf.',
  askForAiCredits: 'Gofynnwch i weinyddwr eich gweithle ychwanegu credydau AI.',
  addAiCredits: 'Ychwanegu credydau AI',
  addAiCreditsLabel: 'Ychwanegu credydau AI (yn agor mewn tab newydd)',
  checkAgain: 'Gwirio eto',
  checkingCredits: 'Yn gwirio credydau…',

  // Welsh counts take the singular noun; "dau" (2) softens munud to funud.
  seconds: (count: number) => `${count} eiliad`,
  minutes: (count: number) => `${count} ${count === 2 ? 'funud' : 'munud'}`,
  aboutTime: (time: string) => `tua ${time}`,
  timeLeft: (time: string) => `Tua ${time} ar ôl`,
  readyIn: (aboutTime: string) => `Yn barod mewn ${aboutTime}`,
  almostReady: 'Bron yn barod…',
  almostDone: 'Bron â gorffen…',
  takingLonger: 'Mae’n cymryd ychydig yn hirach na’r arfer',
  thisOne: (time: string) => `Tua ${time} ar gyfer yr un hwn`,
  thisOneAlmostDone: 'Mae’r un hwn bron â gorffen',
  thisOneAndAll: (thisOne: string, aboutTime: string, count: number) =>
    `${thisOne} · ${aboutTime} ar gyfer ${
      count === 2 ? 'y ddau' : `y ${count} i gyd`
    }`,
  allReels: (time: string, count: number) =>
    count === 2
      ? `Tua ${time} ar gyfer y ddau microreel`
      : `Tua ${time} ar gyfer y ${count} microreel i gyd`,

  // The page head, status, renaming and microreel list, as above.
  allMicroreels: 'Pob microreel',
  beta: 'Beta',
  betaFeature: 'Nodwedd beta',

  statusDrafting: 'Drafftio',
  statusChecking: 'Gwirio',
  statusChooseTopics: 'Dewis pynciau',
  statusStoryboardReady: 'Bwrdd stori yn barod',
  statusApproved: 'Wedi’i gymeradwyo',
  statusGenerating: 'Cynhyrchu',
  statusReady: 'Yn barod',
  statusFailed: 'Wedi methu',

  workCanceling: 'Yn canslo…',
  workDrafting: 'Yn drafftio’r bwrdd stori…',
  workStarting: 'Yn dechrau cynhyrchu…',
  workGenerating: 'Yn cynhyrchu…',
  workFinishing: 'Yn gorffen…',
  working: 'Wrthi’n gweithio…',
  stageStarting: 'Dechrau cynhyrchu',
  stageFinishing: 'Gorffen',
  stageExporting: 'Allforio microreel ',
  stageTranscribingVideo: 'Trawsgrifio’r fideo',
  stageFindingVideoTopics: 'Dod o hyd i’r pynciau yn y fideo',
  readyCount: (ready: number, total: number) => `${ready} o ${total} yn barod`,
  cancel: 'Canslo',
  cancelGeneration: 'Canslo’r cynhyrchu',

  // "Ei gadw" (it, a microreel) and "eu cadw" (them).
  finishMicroreels: 'Gorffen eich microreels',
  finishHint: (ready: number, count: number, remaining: number) =>
    `Mae ${ready} o ${count} microreel yn barod. Bydd cynhyrchu yn ${
      ready === 1 ? 'ei gadw' : 'eu cadw'
    } ac yn gwneud ${
      remaining === 1 ? 'yr un sy’n weddill' : `y ${remaining} sy’n weddill`
    }.`,
  // "Ei orffen" (it, a microreel) and "eu gorffen" (them).
  finishAll: (count: number) =>
    count === 1
      ? 'Bydd cynhyrchu yn cadw eich microreel ac yn ei orffen.'
      : count === 2
      ? 'Bydd cynhyrchu yn cadw’r ddau microreel ac yn eu gorffen.'
      : `Bydd cynhyrchu yn cadw’r ${count} microreel i gyd ac yn eu gorffen.`,
  generateRemaining: (remaining: number) =>
    remaining === 1
      ? 'Cynhyrchu’r microreel sy’n weddill'
      : `Cynhyrchu’r ${remaining} microreel sy’n weddill`,
  madeKept: (ready: number) =>
    ready === 1
      ? 'Caiff y microreel a wnaed eisoes ei gadw.'
      : 'Caiff y microreels a wnaed eisoes eu cadw.',
  // "Ei orffen" (it, a microreel) and "eu gorffen" (them).
  continueGenerating: 'Parhau',
  continuingGeneration: 'Yn parhau…',
  continueMakes: (remaining: number, kept: number) =>
    remaining === 0
      ? kept === 1
        ? 'Bydd Parhau yn ei orffen.'
        : 'Bydd Parhau yn eu gorffen.'
      : remaining === 1
      ? 'Bydd Parhau yn gwneud yr un sy’n weddill yn unig.'
      : `Bydd Parhau yn gwneud y ${remaining} sy’n weddill yn unig.`,
  // Welsh counts take the singular noun: "tua 10 gair".
  ranPastEstimate: (
    title: string,
    seconds: number,
    estimate: number,
    cut?: number,
  ) =>
    `Mae “${title}” yn para ${seconds}s, yn hirach na’r ${estimate}s a amcangyfrifwyd, a daeth y cynhyrchu i ben yno.${
      cut
        ? ` Os bydd y cynhyrchu yn stopio yno eto, torrwch tua ${cut} gair o’i naratif.`
        : ''
    }`,

  renameMicroreel: 'Ailenwi’r microreel',
  renameMicroreels: 'Ailenwi’r microreels',
  microreelName: 'Enw’r microreel',
  microreelsName: 'Enw’r microreels',
  enterMicroreelName: 'Rhowch enw ar gyfer y microreel.',
  enterMicroreelsName: 'Rhowch enw ar gyfer y microreels.',

  nowPlaying: 'Yn chwarae nawr',
  editing: 'Yn golygu',
  switchMicroreel: 'Newid microreel',
  previousMicroreel: 'Microreel blaenorol',
  nextMicroreel: 'Microreel nesaf',
  pagerPosition: (current: number, count: number) => `${current} / ${count}`,

  sceneNoun: 'golygfa',
  clipNoun: 'clip',
  blockerLabel: (reel: number, title?: string, noun?: string, scene?: number) =>
    `Microreel ${reel}${title ? ` (${title})` : ''}${
      noun && scene ? `, ${noun} ${scene}` : ''
    }`,
  confirmFlaggedBlocker:
    'Cadarnhewch yr eitem a nodwyd ar frig ei fwrdd stori.',
};

export const studioText: typeof english = getLocalizationBasedOnLang(
  english,
  welsh,
);
